import type { MouseEvent } from 'react';

/**
 * Did this click land on the BACKGROUND of a card or a row — the part that should open the posting?
 *
 * Both views make the whole surface clickable, and both need the same two exceptions, so the rule
 * is written once. Two hand-written versions of "was this click mine?" is how a card and a row end
 * up disagreeing about what counts as a click on the same posting.
 *
 * NOT a click on an inner control. The title is still a real `<button>` — it is the keyboard path
 * and the visible affordance — and its own handler already opened the panel by the time the click
 * bubbles up here. Without this the container would open the same posting a second time.
 *
 * NOT the end of a text selection. Dragging across a publisher's name to copy it finishes with a
 * click on the card, and a panel that opens every time someone selects text makes the text
 * unselectable in practice.
 */
export function isBackgroundClick(event: MouseEvent<HTMLElement>): boolean {
  const target = event.target;
  if (target instanceof Element) {
    // Bounded to the container: `closest` otherwise walks past it, all the way to the document.
    const control = target.closest('button, a, input, select, textarea, label');
    if (control && event.currentTarget.contains(control)) return false;
  }

  const selection = typeof window === 'undefined' ? null : window.getSelection();
  return selection === null || selection.isCollapsed;
}
